import { useState, useEffect } from "react";
import axios from "axios";
import { 
  DocumentTextIcon, 
  PhotoIcon, 
  PencilIcon, 
  TrashIcon 
} from "@heroicons/react/24/outline";

// Définition dynamique de l'URL backend
const BACKEND_URL =
  window.location.hostname === "localhost"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PROD;

export default function DocumentGrid({ categorie }) {
  const [documents, setDocuments] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formData, setFormData] = useState({ nom: "", lien: "", type: "pdf", categorie });

  useEffect(() => {
    fetchDocuments();
  }, [categorie]);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ressources?categorie=${categorie}`);
      setDocuments(res.data);
    } catch (err) {
      console.error("❌ Erreur lors du chargement des documents :", err);
    }
  };

  const openModal = (doc = null) => {
    setEditingDoc(doc);
    setFormData(doc 
      ? { nom: doc.nom, lien: doc.lien, type: doc.type, categorie: doc.categorie } 
      : { nom: "", lien: "", type: "pdf", categorie });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingDoc) {
        await axios.put(`${BACKEND_URL}/api/ressources/${editingDoc._id}`, formData);
      } else {
        await axios.post(`${BACKEND_URL}/api/ressources`, formData);
      }
      setModalOpen(false);
      fetchDocuments();
    } catch (err) {
      console.error("❌ Erreur lors de l'enregistrement :", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Voulez-vous vraiment supprimer ce document ?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ressources/${id}`);
      fetchDocuments();
    } catch (err) {
      console.error("❌ Erreur lors de la suppression :", err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* 📄 Affichage des documents sans barre de recherche ni filtres */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {documents.map((doc) => (
          <div key={doc._id} className="relative flex flex-col bg-white rounded-lg shadow-md overflow-hidden">
            <div className="h-48 bg-gray-200 flex items-center justify-center">
              {doc.type === "pdf" ? (
                <embed src={`https://mon-vps.ovh.net/previews/${doc.lien}`} className="w-full h-full object-cover" />
              ) : (
                <PhotoIcon className="w-16 h-16 text-gray-500" />
              )}
            </div>

            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-2">
                {doc.type === "pdf" ? (
                  <DocumentTextIcon className="w-6 h-6 text-blue-500" />
                ) : (
                  <PhotoIcon className="w-6 h-6 text-green-500" />
                )}
                <a
                  href={`https://mon-vps.ovh.net/${doc.lien}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline truncate max-w-[150px]"
                >
                  {doc.nom}
                </a>
              </div>

              <div className="flex space-x-2">
                <button 
                  onClick={() => openModal(doc)}
                  className="p-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg shadow-md"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleDelete(doc._id)}
                  className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow-md"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 📌 Modal d'édition uniquement */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-96">
            <h2 className="text-lg font-semibold mb-4">{editingDoc ? "Modifier le document" : "Ajouter un document"}</h2>

            <label className="block text-sm font-medium text-gray-700">Nom du document</label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              className="w-full p-2 border rounded-lg mb-3"
            />

            <label className="block text-sm font-medium text-gray-700">Lien du document</label>
            <input
              type="text"
              value={formData.lien}
              onChange={(e) => setFormData({ ...formData, lien: e.target.value })}
              className="w-full p-2 border rounded-lg mb-3"
            />

            <div className="flex justify-end space-x-3">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">Annuler</button>
              <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">{editingDoc ? "Modifier" : "Ajouter"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
